import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';

import { Injectable } from '@angular/core';
import {
    DynamoDBClient, ExecuteStatementCommand, ExecuteStatementCommandInput,
    ExecuteStatementCommandOutput
} from '@aws-sdk/client-dynamodb';
import { AwsCredentialIdentity } from '@aws-sdk/types';

import { Region } from '../../../../shared/aws-consts';
import { Tables } from '../../../../shared/consts';
import { environment } from '../../environments/environment';
import { ControlData, ControlDetail } from '../shared/app-consts';

@Injectable({
  providedIn: 'root'
})
export class DynamoDbService {
  #client!: DynamoDBClient;
  get #now(): string {
    const currentDate = new Date();
    return currentDate.toISOString();
  }

  set credentials(credentials: AwsCredentialIdentity) {
    this.#client = new DynamoDBClient({
      apiVersion: environment.aws.apiVersion,
      region: Region.Tokyo,
      credentials,
    });
  }

  #sendStatement(statement: string): Observable<ExecuteStatementCommandOutput> {
    const request: ExecuteStatementCommandInput = { Statement: statement };
    return from(this.#client.send(new ExecuteStatementCommand(request)))
      .pipe(
        catchError(err => {
          console.error(err);
          return throwError(() => new Error(' Error: ' + err.message));
        })
      );
  }

  fetchAccountTable(): Observable<ExecuteStatementCommandOutput> {
    return this.#sendStatement(`SELECT * FROM "${Tables.Account}" WHERE "ScoreCalculationTargetFlag" = 1 AND AccountID IS NOT NULL`);
  }

  registerTeamName(accountId: string, newTeamName: string): Observable<ExecuteStatementCommandOutput> {
    return this.#sendStatement(`UPDATE "${Tables.Account}" SET "TeamName" = '${newTeamName}' WHERE "AccountID" = '${accountId}' AND "ScoreCalculationTargetFlag" = 1`);
  }

  get fetchControlDetail(): Observable<ControlDetail[]> {
    return this.#sendStatement(`SELECT * FROM "${Tables.Control}"`)
      .pipe(
        map((output: ExecuteStatementCommandOutput) => this.#parseControlDetail(output))
      );
  }
  fetchControlDetailByAccountID(accountId: string): Observable<ControlDetail[]> {
    return this.#sendStatement(`SELECT * FROM "${Tables.Control}" WHERE "AccountID" = '${accountId}'`)
      .pipe(
        map((output: ExecuteStatementCommandOutput) => this.#parseControlDetail(output))
      );
  }
  #parseControlDetail(output: ExecuteStatementCommandOutput): ControlDetail[] {
    return output.Items!.map((item) => ({
      AccountID: item['AccountID'].S!,
      ControlID: item['ControlID'].S!,
      ControlUnitBaseScore: Number(item['ControlUnitBaseScore'].N!),
      Status: Number(item['Status'].N!),
      ApplicationTimestamp: item['ApplicationTimestamp'].S!,
      ControlDescription: item['ControlDescription'].S!,
      ApplicationCount: item['ApplicationCount'] ? Number(item['ApplicationCount'].N!) : undefined,
    }));
  }
  convertControlData(details: ControlDetail[]): ControlData[] {
    return details.map(detail => ({
      ControlID: detail.ControlID,
      Status: detail.Status,
      ApplicationTimestamp: detail.ApplicationTimestamp,
      ApplicationCount: detail.ApplicationCount || 0
    }));
  }

  // fetchControlDetailByControlID(accountId: string, controlId: string): Observable<ControlDetail[]> {
  //   return this.#sendStatement(`SELECT * FROM "${Tables.Control}" WHERE "AccountID" = '${accountId}' AND "ControlID" = '${controlId}'`)
  //     .pipe(
  //       map((output: ExecuteStatementCommandOutput) => this.#parseControlDetail(output))
  //     );
  // }

  updateControlStatus(accountId: string, controlId: string, status: number): Observable<ExecuteStatementCommandOutput> {
    return this.#sendStatement(
      `UPDATE "${Tables.Control}"
      SET "Status" = ${status}
      SET "ApplicationTimestamp" = '${this.#now}'
      SET "ApplicationCount" = "ApplicationCount" + 1
      WHERE "AccountID" = '${accountId}' AND "ControlID" = '${controlId}'`
    );
  }

  // 「Control」テーブルから「ControlUnitBaseScore」の値を取ってきて「Score」に反映、「Status」を「4：準拠」に変更
  doneControl(accountId: string, controlId: string): Observable<ExecuteStatementCommandOutput> {
    return this.#sendStatement(
      `SELECT "ControlUnitBaseScore"
      FROM "${Tables.Control}"
      WHERE "AccountID" = '${accountId}' AND "ControlID" = '${controlId}'`
    ).pipe(
      switchMap((res) => {
        const baseScore = res.Items![0]['ControlUnitBaseScore'].N;
        return this.#sendStatement(
          `UPDATE "${Tables.Control}"
          SET "Status" = 4, "Score" = ${baseScore}
          WHERE "AccountID" = '${accountId}' AND "ControlID" = '${controlId}'`
        );
      })
    );
  }
  undoneControl(accountId: string, controlId: string): Observable<ExecuteStatementCommandOutput> {
    return this.#sendStatement(
      `UPDATE "${Tables.Control}"
      SET "Status" = 0, "Score" = 0
      WHERE "AccountID" = '${accountId}' AND "ControlID" = '${controlId}'`
    );
  }
  assignTeamPoints(accountId: string, points: number): Observable<ExecuteStatementCommandOutput> {
    return this.#sendStatement(
      `UPDATE "${Tables.Account}"
      SET "TotalScore" = if_not_exists("TotalScore", 0) + ${points}
      WHERE "ScoreCalculationTargetFlag" = 1 AND "AccountID" = '${accountId}'`
    );
  }
}
